import { useEffect, useState } from "react";
import { Heart, Gift, Camera, PartyPopper, TrendingUp, Sparkles } from "lucide-react";

const mockMoments = [
  {
    type: "birthday",
    text: "Léa fête ses 5 ans !",
    time: "2 min",
    contributions: 18,
    amount: "€450",
    futureImpact: "€580 à 18 ans",
    icon: PartyPopper,
  },
  {
    type: "contribution",
    text: "8 oncles et tantes ont contribué au permis de Tom",
    time: "3h",
    contributions: 8,
    amount: "€1,200",
    futureImpact: "€1,320 à 18 ans",
    icon: Gift,
  },
  {
    type: "birthday",
    text: "Sophie fête ses 3 ans",
    time: "7h",
    contributions: 22,
    amount: "€680",
    futureImpact: "€870 à 18 ans",
    icon: PartyPopper,
  },
  {
    type: "contribution",
    text: "15 proches ont contribué aux vacances de Marie",
    time: "12h",
    contributions: 15,
    amount: "€2,100",
    futureImpact: "€2,420 à 18 ans",
    icon: Gift,
  },
  {
    type: "contribution",
    text: "Rentrée scolaire de Lucas : 12 contributions",
    time: "18h",
    contributions: 12,
    amount: "€840",
    futureImpact: "€1,050 à 18 ans",
    icon: Gift,
  },
  {
    type: "birthday",
    text: "10 ans de Chloé : cagnotte pour son voyage scolaire",
    time: "1j",
    contributions: 28,
    amount: "€1,600",
    futureImpact: "€1,840 à 18 ans",
    icon: PartyPopper,
  },
  {
    type: "contribution",
    text: "Premiers cours de musique d'Alexis : 9 contributeurs",
    time: "1j",
    contributions: 9,
    amount: "€520",
    futureImpact: "€650 à 18 ans",
    icon: Gift,
  },
  {
    type: "contribution",
    text: "20 proches mobilisés pour le permis de Mathilde",
    time: "2j",
    contributions: 20,
    amount: "€2,400",
    futureImpact: "€2,650 à 18 ans",
    icon: Gift,
  },
];

const LiveMomentsFeed = () => {
  const [moments, setMoments] = useState(mockMoments);
  const [recentlyAdded, setRecentlyAdded] = useState<number[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate a new moment appearing
      const randomMoment = mockMoments[Math.floor(Math.random() * mockMoments.length)];
      const newMoment = {
        ...randomMoment,
        time: "À l'instant",
      };

      setMoments((prev) => {
        const updated = [newMoment, ...prev.slice(0, 7)];
        setRecentlyAdded([0]);
        setTimeout(() => setRecentlyAdded([]), 3000);
        return updated;
      });
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="section-padding bg-gradient-to-b from-white to-tenderness-light">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Ils alimentent SA cagnotte chaque jour</h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            En ce moment, des familles immortalisent leurs moments et construisent l'avenir de leurs enfants
          </p>
          <div className="inline-flex items-center gap-2 mt-4 bg-growth/20 px-4 py-2 rounded-full">
            <div className="w-2 h-2 rounded-full bg-growth animate-pulse" />
            <span className="text-sm font-medium">En direct</span>
          </div>
        </div>

        {/* Masonry Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {moments.map((moment, index) => {
            const Icon = moment.icon;
            const isRecent = recentlyAdded.includes(index);

            return (
              <div
                key={`${moment.text}-${index}`}
                className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer relative overflow-hidden ${
                  isRecent ? "animate-slide-up-fade animate-breathing" : ""
                }`}
              >
                {/* Moment Type Badge */}
                <div className="absolute top-3 right-3">
                  {moment.type === "birthday" && (
                    <div className="bg-tenderness/20 text-tenderness-dark px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Anniversaire
                    </div>
                  )}
                  {moment.type === "contribution" && (
                    <div className="bg-growth/20 text-growth-dark px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Cagnotte
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-3 mb-3 mt-6">
                  <div className="w-10 h-10 rounded-full bg-legatia-green flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm line-clamp-2">{moment.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{moment.time}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-sm mb-3">
                  {moment.contributions && (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Gift className="h-4 w-4" />
                      <span>{moment.contributions} contributeurs</span>
                    </div>
                  )}
                </div>

                {/* Amount + Future Impact */}
                {moment.amount && (
                  <div className="space-y-2">
                    <div className="bg-primary/10 px-3 py-2 rounded-lg flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Aujourd'hui</span>
                      <p className="text-sm font-bold text-primary">{moment.amount}</p>
                    </div>
                    
                    {moment.futureImpact && (
                      <div className="bg-gradient-to-r from-growth-light to-future-light px-3 py-2 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3 text-growth" />
                          <span className="text-xs font-semibold text-growth">Impact futur</span>
                        </div>
                        <p className="text-sm font-bold text-growth">{moment.futureImpact}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Hover overlay */}
                <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    className="w-full bg-primary/10 hover:bg-primary/20 text-primary py-2 px-4 rounded-lg text-sm font-medium transition-all"
                    onClick={() => window.location.href = "https://app.getlegatia.ch/"}
                  >
                    Créer SA cagnotte
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LiveMomentsFeed;
